import axios from "axios";
import {
  HttpStatus,
  TRANSIENT_HTTP_STATUSES,
  isSuccessStatus,
} from "../constants/httpStatus";
import {
  ApiClientError,
  ApiEnvelope,
  getApiErrorMessage,
  isAuthError,
  parseApiEnvelopeError,
  parseApiResponse,
} from "../utils/apiResponse";
import { authorizationHeaders, redirectToLogin } from "../utils/authSession";
import { mapCustomerPath } from "../utils/pathMap";

type DefaultApiParams = {
  data?: any;
  headers?: any;
  method: string;
  init?: RequestInit;
  withCredentials?: boolean;
};

export type ApiParams = {
  data?: any;
  method: string;
  headers?: Record<string, string>;
  init?: RequestInit;
};

const inflightGetRequests = new Map<string, Promise<unknown>>();

function serviceAuthHeaders(): Record<string, string> {
  const serviceId = (process.env.REACT_APP_SERVICE_ID || "").trim();
  return {
    ...(serviceId && { "x-service-id": serviceId }),
  };
}

async function callApiWithDefaults(
  url: string,
  params: DefaultApiParams
) {
  return axios({
    url,
    ...params,
    validateStatus: () => true,
  });
}

function throwHttpError(message: string, status: number, response: unknown): never {
  const error = new Error(message) as Error & {
    status?: number;
    response?: unknown;
  };
  error.status = status;
  error.response = response;
  throw error;
}

function redirectToLoginIfUnauthorized(httpStatus: number, message: string) {
  if (isAuthError(httpStatus, message)) {
    redirectToLogin();
  }
}

async function requestCustomerApi(path: string, params: ApiParams) {
  return callApiWithDefaults(
    `${process.env.REACT_APP_SERVER_URI}${mapCustomerPath(path)}`,
    {
      method: params.method,
      data: params.data,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        ...serviceAuthHeaders(),
        ...authorizationHeaders(),
        ...(params?.headers as Record<string, string> | undefined),
        ...(params?.init?.headers as Record<string, string> | undefined),
      },
    }
  );
}

function validateApiResponseBody(
  resObj: { status: number; data: unknown }
): ApiEnvelope {
  const httpStatus = resObj.status as number;
  const raw = resObj.data;

  // Gin/proxy errors often return plain text (e.g. "404 page not found") with no { err, data } body.
  if (typeof raw !== "object" || raw === null) {
    if (!isSuccessStatus(httpStatus)) {
      throwHttpError(
        typeof raw === "string" && raw.trim()
          ? raw
          : `Request failed with status ${httpStatus}`,
        httpStatus,
        resObj
      );
    }
    return {};
  }

  const res = raw as ApiEnvelope;

  if (res?.statusCode === HttpStatus.NOT_FOUND || httpStatus === HttpStatus.NOT_FOUND) {
    throwHttpError(
      getApiErrorMessage(res) || "Not found",
      HttpStatus.NOT_FOUND,
      resObj
    );
  }

  const errMessage = getApiErrorMessage(res);
  const hasStandardError =
    Boolean(res?.error?.type) ||
    Boolean(res?.error?.message) ||
    (httpStatus >= HttpStatus.BAD_REQUEST && Boolean(res?.error));
  if (res.err || res.success === false || hasStandardError) {
    redirectToLoginIfUnauthorized(httpStatus, errMessage);

    const errStatus = (res.err as { status?: number })?.status || 0;
    if (TRANSIENT_HTTP_STATUSES.includes(errStatus as HttpStatus)) {
      return res.err as unknown as ApiEnvelope;
    }

    throw new ApiClientError(parseApiEnvelopeError(res, httpStatus), resObj);
  }

  return res;
}

async function executeCallApi(path: string, params: ApiParams): Promise<any> {
  const resObj: any = await requestCustomerApi(path, params);
  validateApiResponseBody(resObj);
  return parseApiResponse(resObj);
}

/**
 * Like `callApi` but returns the full JSON body. Use for list endpoints that
 * expose `total` beside `data` (e.g. GET /batch/tasks TaskListResult).
 */
async function executeCallApiEnvelope(
  path: string,
  params: ApiParams
): Promise<ApiEnvelope & Record<string, unknown>> {
  const resObj: any = await requestCustomerApi(path, params);
  return validateApiResponseBody(resObj) as ApiEnvelope & Record<string, unknown>;
}

export async function callApiEnvelope(
  path: string,
  params: ApiParams
): Promise<ApiEnvelope & Record<string, unknown>> {
  if (params.method.toUpperCase() !== "GET") {
    return executeCallApiEnvelope(path, params);
  }

  const requestKey = mapCustomerPath(path);
  const inflight = inflightGetRequests.get(requestKey) as
    | Promise<ApiEnvelope & Record<string, unknown>>
    | undefined;
  if (inflight) {
    return inflight as Promise<ApiEnvelope & Record<string, unknown>>;
  }

  const request = executeCallApiEnvelope(path, params).finally(() => {
    inflightGetRequests.delete(requestKey);
  });
  inflightGetRequests.set(requestKey, request);
  return request;
}

export async function callApi(path: string, params: ApiParams): Promise<any> {
  if (params.method.toUpperCase() !== "GET") {
    return executeCallApi(path, params);
  }

  const requestKey = mapCustomerPath(path);
  const inflight = inflightGetRequests.get(requestKey);
  if (inflight) {
    return inflight;
  }

  const request = executeCallApi(path, params).finally(() => {
    inflightGetRequests.delete(requestKey);
  });
  inflightGetRequests.set(requestKey, request);
  return request;
}

axios.defaults.baseURL = process.env.REACT_APP_SERVER_URI;
axios.defaults.withCredentials = true;

/** Multipart POSTs (batch upload) — do not set JSON Content-Type. */
export async function callAxiosPostApi(url: string, data?: unknown) {
  try {
    const response = await axios.post(mapCustomerPath(url), data, {
      withCredentials: true,
      headers: {
        ...serviceAuthHeaders(),
        ...authorizationHeaders(),
      },
    });
    return response.data;
  } catch (err) {
    throw (err as any)?.response?.data || err;
  }
}
